<template>
  <Drawer
    :id="drawerId"
    title="Project Details"
    :loading="loading"
    padded
  >
    <div
      v-if="project"
      @paste="onPaste"
    >
      <!-- Show any rejection note that may exist -->
      <v-row
        v-if="project.rejectionNotes && !completed"
        dense
        class="mb-3"
      >
        <v-col cols="12">
          <v-alert
            text
            dense
            outlined
            color="error"
            class="body-1 mb-0"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                Additional work required.
              </div>
              <v-btn
                text
                @click="showRejectionNote"
              >
                View
              </v-btn>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Basic project details -->
      <div class="mb-5">
        <div
          v-if="project.designGroup && project.category"
          class="text-h6"
        >
          {{ project.designGroup.nameHierarchy }} {{ project.category.namePlural }}
        </div>

        <IconList
          :items="iconList"
          class="mt-2"
        />

        <div v-if="project.context">
          <div class="text-h6 mt-4">
            Notes
          </div>
          <div class="body-2">
            {{ project.context }}
          </div>
        </div>
      </div>
      <v-divider class="my-5" />

      <!-- Upload Row -->
      <div v-if="(claimed || underReview) && !completed">
        <v-row
          align="center"
        >
          <v-col cols="12">
            <v-text-field
              v-if="claimed || (underReview && project.rejectionNotes)"
              :value="paste"
              dense
              outlined
              color="primary"
              accept=".jpg,.png,.jpeg"
              label="Paste Images Here"
              hide-details="auto"
              class="body-2"
              readonly
              clearable
              @click:clear="pastedFiles = []"
            />
          </v-col>
          <v-col
            cols="12"
          >
            <v-file-input
              v-if="claimed || (underReview && project.rejectionNotes)"
              v-model="selectedFiles"
              dense
              outlined
              color="primary"
              accept=".jpg,.png,.jpeg"
              label="Upload Images Here"
              hide-details="auto"
              multiple
              prepend-icon=""
              class="body-2"
              clearable
            />
          </v-col>
          <v-col
            v-if="files.length"
            cols="12"
          >
            <v-btn
              elevation="0"
              color="primary"
              block
              large
              :loading="processing"
              @click="upload"
            >
              Submit<v-icon class="ml-2">
                {{ uploadIcon }}
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-divider class="mt-5 mb-2" />
      </div>

      <!-- Row for releasing-->
      <v-row
        v-if="!completed"
        no-gutters
        align="center"
      >
        <v-col
          v-if="claimed"
          cols="12"
        >
          <v-btn
            color="error"
            outlined
            elevation="0"
            block
            class="mr-3 mt-3"
            large
            rounded
            :disabled="processing"
            @click="release"
          >
            Release
          </v-btn>
        </v-col>
      </v-row>

      <!-- Claim button-->
      <v-row
        v-if="!claimed && !completed"
        no-gutters
      >
        <v-col
          v-if="available"
          cols="12"
        >
          <v-btn
            color="primary"
            elevation="0"
            block
            class="my-5"
            large
            rounded
            :disabled="processing"
            @click="claim"
          >
            Claim
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </Drawer>
</template>

<script>
import { ASSET_PROJECT_STATUS, DRAWERS } from '../../../utils/constants';
import Drawer from '../../layout/Drawer';
import { cloneDeep } from 'lodash';
import IconList from '../../utilities/IconList';
import { mdiClockOutline, mdiCurrencyUsd, mdiCloudUpload, mdiNote, mdiLink } from '@mdi/js';

export default {
  name: 'ImageProjectDrawer',

  components: {
    Drawer,
    IconList
  },

  data () {
    return {
      project: {},
      loading: false,
      drawerId: DRAWERS.CONTRIBUTOR_IMAGE_PROJECT_DRAWER,
      processing: false,
      uploadIcon: mdiCloudUpload,
      pastedFiles: [],
      selectedFiles: []
    };
  },

  computed: {
    files () {
      let list = [];
      if (this.pastedFiles && this.pastedFiles.length) {
        list = list.concat(this.pastedFiles);
      }
      if (this.selectedFiles && this.selectedFiles.length) {
        list = list.concat(this.selectedFiles);
      }
      return list;
    },

    paste () {
      if (this.pastedFiles && this.pastedFiles.length) {
        if (this.pastedFiles.length === 1) {
          return this.pastedFiles[0].name;
        }
        return `${this.pastedFiles.length} files`;
      }
      return '';
    },

    drawerOpenedAt () {
      return this.$store.state.drawers.lastOpenedAt;
    },

    claimed () {
      return this.project.inspirationalImageStatusId === ASSET_PROJECT_STATUS.ASSIGNED;
    },

    available () {
      return this.project.inspirationalImageStatusId === ASSET_PROJECT_STATUS.OPEN;
    },

    underReview () {
      return this.project.inspirationalImageStatusId === ASSET_PROJECT_STATUS.UNDER_REVIEW;
    },

    completed () {
      return this.project.paid;
    },

    iconList () {
      var list = [{
        text: this.$options.filters.shortDateWithTime(this.project.createdDate),
        icon: mdiClockOutline
      }];
      if (this.project.price) {
        list.push({
          text: this.$options.filters.currency(this.project.price),
          icon: mdiCurrencyUsd
        });
      }
      list.push({
        text: this.project.category?.defaultCoreProductName,
        icon: mdiNote
      });
      list.push({
        text: `<a href="${this.project.category?.catalogUrl}" target="_blank">View Templates</a>`,
        icon: mdiLink
      });
      return list;
    }
  },

  watch: {
    drawerOpenedAt (val) {
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) {
        this.loading = true;
        this.$store.dispatch('contributorImageProjects/getDetail', this.$store.state.contributorImageProjects.selectedProjectId)
          .then(project => {
            this.project = project;
            this.selectedFiles = [];
            this.pastedFiles = [];
          })
          .finally(() => {
            setTimeout(() => {
              this.loading = false;
            }, 500);
          });
      }
    }
  },

  methods: {
    release () {
      this.processing = true;
      this.$store.dispatch('contributorImageProjects/release', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    claim () {
      this.processing = true;
      this.$store.dispatch('contributorImageProjects/claim', this.project.id)
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .catch(error => {
          this.$root.$alert('Unable to claim project', error.response.data.exceptionMessage, { hideSupport: true, cancelText: '' });
          console.log(error);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    upload () {
      this.processing = true;
      this.$store.dispatch('contributorImageProjects/uploadImages', {
        id: this.project.id,
        files: this.files
      })
        .then((project) => {
          this.project = Object.assign(project, cloneDeep(this.project));
          this.$store.commit('drawers/CLOSE_DRAWER', this.drawerId);
        })
        .finally(() => {
          this.processing = false;
        });
    },

    showRejectionNote () {
      this.$root.$alert('Errors Found', this.project.rejectionNotes, { hideSupport: true, cancelText: '' });
    },

    onPaste (event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const imageFile = items[i].getAsFile();
          this.pastedFiles.push(imageFile);
        }
      }
    }
  }
};
</script>

  <style scoped>
  </style>
